import * as Sentry from '@sentry/react';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TokenAuthProvider } from './providers/TokenAuthProvider';
import { AmplitudeProvider } from './providers/AmplitudeProvider';
import { GTMProvider } from './providers/GTMProvider';
import { FacebookPixelProvider } from './providers/FacebookPixelProvider';
import { AnalyticsErrorBoundary } from './components/AnalyticsErrorBoundary';
import { hasAssignedEnterpriseAdminAccess } from './utils/enterpriseQuery';
import { persistAttributionParams } from './utils/attributionParams';
import {
  clearChunkReloadAttempt,
  reloadOnceForStaleChunk,
} from './utils/chunkReload';

persistAttributionParams();

// Retry dynamic import on failure — handles stale chunks after deploys.
// When Vercel serves a new build, old chunk hashes no longer exist.
// The server returns index.html (SPA fallback) instead of JS, causing
// "SyntaxError: Unexpected token '<'". A full reload fetches the new index.html
// which references the correct chunk hashes.
function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    importFn().catch(() => {
      if (reloadOnceForStaleChunk()) {
        // Keep Suspense pending while the browser reloads. Do not throw —
        // an unhandled "Chunk reload timed out" was flooding Sentry when
        // the reload was slow or blocked (extensions / flaky network).
        return new Promise<{ default: React.ComponentType<any> }>(() => {
          /* never resolves; page reload owns the next navigation */
        });
      }
      clearChunkReloadAttempt();
      return importFn();
    })
  );
}

// Lazy load route components for better initial load time
// This reduces the initial bundle size by ~40-60%
const Login = lazyWithRetry(() => import('./components/auth/Login'));
const ForgotPassword = lazyWithRetry(() => import('./components/auth/ForgotPassword'));
const VerifyResetCode = lazyWithRetry(() => import('./components/auth/VerifyResetCode'));
const ResetPassword = lazyWithRetry(() => import('./components/auth/ResetPassword'));
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'));
const FunnelBFlow = lazyWithRetry(() => import('./components/funnel-b/OnboardingFlow'));
const FriendsFlow = lazyWithRetry(() => import('./components/friends/FriendsFlow'));
const FriendsManagePage = lazyWithRetry(() => import('./pages/FriendsManagePage'));
const FriendsMembershipPage = lazyWithRetry(() => import('./pages/FriendsMembershipPage'));
const OnboardingResume = lazyWithRetry(() => import('./components/funnel-b/OnboardingResume'));
const MemberOnboardingFlow = lazyWithRetry(() => import('./components/member-onboarding/MemberOnboardingFlow'));
const AdminInvitePage = lazyWithRetry(() => import('./pages/AdminInvitePage'));
const SuperAdminInvitePage = lazyWithRetry(() => import('./pages/SuperAdminInvitePage'));
const EnterpriseAdminInvitePage = lazyWithRetry(() => import('./pages/EnterpriseAdminInvitePage'));
const WebLeaderboardPage = lazyWithRetry(() => import('./pages/WebLeaderboardPage'));
const PublicLeaderboardPage = lazyWithRetry(() => import('./pages/PublicLeaderboardPage'));
const PublicWallOfLovePage = lazyWithRetry(() => import('./pages/PublicWallOfLovePage'));
const ChallengeAnalyticsPage = lazyWithRetry(() => import('./pages/ChallengeAnalyticsPage'));
const EnterprisePage = lazyWithRetry(() => import('./pages/EnterprisePage'));
const EnterpriseTeamDetailPage = lazyWithRetry(() => import('./pages/EnterpriseTeamDetailPage'));

// Loading fallback component - shown while lazy components load
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Protected route - requires authentication only
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AnalyticsProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AnalyticsErrorBoundary fallback={<>{children}</>}>
    <AmplitudeProvider apiKey={process.env.REACT_APP_AMPLITUDE_API_KEY || ''}>
      <GTMProvider gtmId={process.env.REACT_APP_GTM_ID || ''}>
        <FacebookPixelProvider pixelId={process.env.REACT_APP_PIXEL_ID || ''}>
          {children}
        </FacebookPixelProvider>
      </GTMProvider>
    </AmplitudeProvider>
  </AnalyticsErrorBoundary>
);

// Enterprise HR dashboard - requires enterprise admin access
const EnterpriseProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user: authUser } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const hasEnterpriseAdminAccess = hasAssignedEnterpriseAdminAccess(authUser);

  if (!hasEnterpriseAdminAccess) {
    return <Navigate to="/account" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AnalyticsProviders>
          <TokenAuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Default signup funnel at app.getwemove.com */}
              <Route path="/" element={<FunnelBFlow />} />
              <Route path="/funnel-b" element={<FunnelBFlow />} />
              <Route path="/friends" element={<FriendsFlow />} />
              <Route path="/friends/manage" element={<FriendsManagePage />} />
              <Route
                path="/account/membership"
                element={
                  <ProtectedRoute>
                    <FriendsMembershipPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/onboarding/resume" element={<OnboardingResume />} />

              {/* Login route */}
              <Route path="/login" element={<Login />} />

              {/* Password reset routes */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-reset-code" element={<VerifyResetCode />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Member onboarding - public route */}
              <Route path="/members" element={<MemberOnboardingFlow />} />

              {/* Admin invite - public route */}
              <Route path="/admin-invite" element={<AdminInvitePage />} />
              <Route path="/super-admin-invite" element={<SuperAdminInvitePage />} />
              <Route path="/enterprise-admin-invite" element={<EnterpriseAdminInvitePage />} />

              {/* Public leaderboard - no auth required */}
              <Route path="/leaderboard/public/:challengeId" element={<PublicLeaderboardPage />} />

              {/* Public Wall of Love - no auth required */}
              <Route path="/wall-of-love/:slug" element={<PublicWallOfLovePage />} />

              {/* Web leaderboard - requires authentication */}
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <WebLeaderboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Account management - requires authentication */}
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />

              {/* Enterprise HR dashboard */}
              <Route
                path="/enterprise"
                element={
                  <EnterpriseProtectedRoute>
                    <EnterprisePage />
                  </EnterpriseProtectedRoute>
                }
              />
              <Route
                path="/enterprise/teams/:clubId"
                element={
                  <EnterpriseProtectedRoute>
                    <EnterpriseTeamDetailPage />
                  </EnterpriseProtectedRoute>
                }
              />

              {/* Super admin / enterprise admin: full admin dashboard for a managed team */}
              <Route
                path="/admin/clubs/:clubId"
                element={
                  <EnterpriseProtectedRoute>
                    <AccountPage />
                  </EnterpriseProtectedRoute>
                }
              />

              {/* Challenge analytics - admin-only deep dive */}
              <Route
                path="/admin/challenges/:challengeId/analytics"
                element={
                  <ProtectedRoute>
                    <ChallengeAnalyticsPage />
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to onboarding */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </TokenAuthProvider>
      </AnalyticsProviders>
    </AuthProvider>
  </BrowserRouter>
  );
};

export default Sentry.withErrorBoundary(App, {
  fallback: (
    <div className="min-h-screen flex items-center justify-center bg-white font-sans">
      <div className="text-center p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
        <p className="text-gray-500 mb-4">Our team has been notified. Please refresh the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Refresh
        </button>
      </div>
    </div>
  ),
});
